import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { SUPPORTED_LANGS, useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { TESTIDS } from "@/constants/testIds";
import { api } from "@/lib/api";
import OnboardingWizard from "@/components/OnboardingWizard";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import { useWorkspaceTranslation } from "@/locales/workspaceExtras";
import { useRoomCopy } from "@/locales/rooms";
import {
  LayoutDashboard, Briefcase, FileText, Users, LifeBuoy,
  Building2, CreditCard, Globe2, UserCircle, LogOut,
  ChevronDown, ChevronRight,
  PenTool, ClipboardCheck, LibraryBig, FolderClosed, Inbox, ListChecks, Bell, PlugZap,
  CirclePlay, Menu, X, MessageSquare,
} from "lucide-react";

const LANGS = SUPPORTED_LANGS;

export default function DashboardLayout() {
  const roomCopy = useRoomCopy();
  const [roomsEnabled, setRoomsEnabled] = useState(false);
  useEffect(() => { api.capabilities().then((c) => setRoomsEnabled(c.private_deal_rooms === true)).catch(() => {}); }, []);
  const { t, lang, setLang } = useI18n();
  const { tr } = useWorkspaceTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [docsOpen, setDocsOpen] = useState(
    () => loc.pathname.startsWith("/app/drafting") ||
          loc.pathname.startsWith("/app/review") ||
          loc.pathname.startsWith("/app/templates") ||
          loc.pathname.startsWith("/app/files") ||
          loc.pathname.startsWith("/app/intakes")
  );
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = (event) => event.key === "Escape" && setMobileNavOpen(false);
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", close);
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const refresh = () => api.notificationUnreadCount()
      .then((result) => setUnreadNotifications(result.count || 0))
      .catch(() => setUnreadNotifications(0));
    refresh();
    window.addEventListener("td-notifications-changed", refresh);
    return () => window.removeEventListener("td-notifications-changed", refresh);
  }, [loc.pathname]);

  const navigate = [
    { to: "/app/dashboard",       label: t("d_nav_dashboard"),      Icon: LayoutDashboard, testid: TESTIDS.dashNavDashboard },
    { to: "/app/deals",           label: t("d_nav_deals"),          Icon: Briefcase,       testid: TESTIDS.dashNavDeals },
    ...(roomsEnabled ? [{ to: "/app/rooms", label: roomCopy.title, Icon: MessageSquare, testid: "dash-nav-rooms" }] : []),
    { to: "/app/tasks",           label: t("d_nav_tasks"),          Icon: ListChecks,       testid: TESTIDS.dashNavTasks },
    { to: "/app/notifications",   label: t("d_nav_notifications"),  Icon: Bell,             testid: TESTIDS.dashNavNotifications, badge: unreadNotifications },
  ];
  const docsChildren = [
    { to: "/app/drafting",  label: t("d_nav_documents_create"),    Icon: PenTool,        testid: TESTIDS.dashNavStudio },
    { to: "/app/review",    label: t("d_nav_documents_review"),    Icon: ClipboardCheck, testid: TESTIDS.dashNavReview },
    { to: "/app/templates", label: t("d_nav_documents_templates"), Icon: LibraryBig,     testid: TESTIDS.dashNavTemplates },
    { to: "/app/files",     label: t("d_nav_documents_files"),     Icon: FolderClosed,   testid: TESTIDS.dashNavFiles },
    { to: "/app/intakes",   label: t("d_nav_requests"),            Icon: Inbox,          testid: TESTIDS.dashNavRequests },
  ];
  const navigateTail = [
    { to: "/app/counterparties",  label: t("d_nav_counterparties"), Icon: Users,          testid: TESTIDS.dashNavCounterparties },
    { to: "/app/expert",          label: t("d_nav_expert"),         Icon: LifeBuoy,       testid: TESTIDS.dashNavExpert },
  ];
  const settings = [
    { to: "/app/company",         label: t("d_nav_company"),        Icon: Building2,      testid: TESTIDS.dashNavCompany },
    { to: "/app/billing",         label: t("d_nav_billing"),        Icon: CreditCard,     testid: TESTIDS.dashNavBilling },
    { to: "/app/settings",        label: t("d_nav_settings"),       Icon: UserCircle,     testid: TESTIDS.dashNavSettings },
    { to: "/app/organization",    label: t("d_nav_organization"),   Icon: Users,          testid: TESTIDS.dashNavOrganization },
    { to: "/app/integrations",    label: tr("Integrations"),        Icon: PlugZap,        testid: TESTIDS.dashNavIntegrations },
  ];

  const doLogout = async () => { await logout(); nav("/", { replace: true }); };
  const cycleLang = () => {
    const idx = LANGS.indexOf(lang);
    setLang(LANGS[(idx + 1) % LANGS.length]);
  };

  const renderLink = ({ to, label, Icon, testid, badge }) => (
    <NavLink key={to} to={to} data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-6 py-2 text-sm transition-colors ${
          isActive
            ? "bg-[var(--td-bg)] text-[var(--td-gold)] border-l-2 border-[var(--td-gold)]"
            : "text-[var(--td-text-2)] hover:text-[var(--td-text)] border-l-2 border-transparent"
        }`}>
      <Icon size={15} className="shrink-0" />
      <span className="font-serif text-[13px] leading-tight flex-1">{label}</span>
      {!!badge && <span className="min-w-5 h-5 px-1 flex items-center justify-center bg-[var(--td-gold)] text-[var(--td-bg)] font-mono text-[9px]">{badge > 99 ? "99+" : badge}</span>}
    </NavLink>
  );

  const docsIsActive = loc.pathname.startsWith("/app/drafting") ||
                       loc.pathname.startsWith("/app/review") ||
                       loc.pathname.startsWith("/app/templates") ||
                       loc.pathname.startsWith("/app/files") ||
                       loc.pathname.startsWith("/app/intakes");

  return (
    <div className="td-workspace min-h-[100dvh] flex flex-col xl:flex-row" style={{ background: "var(--td-bg)" }}>
      <header className="xl:hidden sticky top-0 z-50 min-h-16 px-3 sm:px-4 gap-2 flex items-center justify-between border-b border-[var(--td-border)] bg-[var(--td-surface)]/95 backdrop-blur">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label={tr("Open navigation")}
          aria-expanded={mobileNavOpen}
          aria-controls="mobile-dashboard-navigation"
          className="shrink-0 w-11 h-11 inline-flex items-center justify-center border border-[var(--td-border)] text-[var(--td-text-2)] active:border-[var(--td-gold)] active:text-[var(--td-gold)]"
        >
          <Menu size={20} />
        </button>
        <Link to="/" aria-label="TradeDocs home" className="flex items-center gap-2.5 min-w-0 flex-1 justify-center hover:text-[var(--td-gold)] transition-colors">
          <div className="w-2 h-2 bg-[var(--td-gold)] rotate-45 shrink-0" />
          <span className="font-serif text-lg truncate">TradeDocs<span className="text-[var(--td-gold)]"> · </span>Desk</span>
        </Link>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/demo"
            aria-label={t("d_nav_demo")}
            className="shrink-0 w-11 h-11 inline-flex items-center justify-center border border-[var(--td-gold)] text-[var(--td-gold)]"
          >
            <CirclePlay size={19} />
          </Link>
          <button
            onClick={doLogout}
            aria-label={t("d_nav_logout")}
            data-testid="dash-nav-logout-mobile"
            className="min-w-11 h-11 px-2 inline-flex items-center justify-center border border-[var(--td-border)] text-[var(--td-text-2)] active:border-red-500 active:text-red-400"
          >
            <LogOut size={17} />
          </button>
        </div>
      </header>

      {mobileNavOpen && (
        <button
          type="button"
          aria-label={tr("Close navigation")}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm xl:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <aside id="mobile-dashboard-navigation"
             className={`td-workspace-sidebar fixed inset-y-0 left-0 z-[70] w-[min(92vw,24rem)] h-[100dvh] shrink-0 flex flex-col hairline-bottom transition-transform duration-200 xl:sticky xl:top-0 xl:z-auto xl:w-80 xl:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
             style={{ background: "var(--td-surface)", borderRight: "1px solid var(--td-border)" }}>
        <div className="p-6 hairline-bottom">
          <Link to="/" aria-label="TradeDocs home" className="flex items-center gap-3 pr-10 hover:text-[var(--td-gold)] transition-colors">
            <div className="w-2.5 h-2.5 bg-[var(--td-gold)] rotate-45" />
            <span className="font-serif text-lg">TradeDocs<span className="text-[var(--td-gold)]"> · </span>Desk</span>
          </Link>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            aria-label={tr("Close navigation")}
            className="xl:hidden absolute top-3 right-3 w-11 h-11 inline-flex items-center justify-center text-[var(--td-text-2)] border border-[var(--td-border)]"
          >
            <X size={19} />
          </button>
          <Link to="/demo" className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-[var(--td-gold)] text-[var(--td-gold)] px-4 py-2.5 text-sm hover:bg-[var(--td-gold)] hover:text-[var(--td-bg)] transition-colors">
            <CirclePlay size={16} /> {t("d_nav_demo")}
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto py-3" aria-label="Primary">
          <div className="mb-4">
            <div className="px-6 pb-2 font-mono text-[9px] uppercase tracking-widest text-[var(--td-text-3)]">
              {t("d_section_navigate")}
            </div>
            <div className="space-y-0.5">
              {navigate.map(renderLink)}
              {/* Documents group with submenu */}
              <button
                type="button"
                onClick={() => setDocsOpen((v) => !v)}
                aria-expanded={docsOpen}
                data-testid="dash-nav-documents-toggle"
                className={`w-full flex items-center gap-3 px-6 py-2 text-sm transition-colors ${
                  docsIsActive
                    ? "text-[var(--td-gold)] border-l-2 border-[var(--td-gold)]"
                    : "text-[var(--td-text-2)] hover:text-[var(--td-text)] border-l-2 border-transparent"
                }`}>
                <FileText size={15} className="shrink-0" />
                <span className="font-serif text-[13px] leading-tight flex-1 text-left">{t("d_nav_documents")}</span>
                {docsOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </button>
              {docsOpen && (
                <div className="ml-6 border-l border-[var(--td-border)]">
                  {docsChildren.map(({ to, label, Icon, testid }) => (
                    <NavLink key={to} to={to} data-testid={testid}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 pl-4 pr-6 py-1.5 text-xs transition-colors ${
                          isActive
                            ? "text-[var(--td-gold)]"
                            : "text-[var(--td-text-3)] hover:text-[var(--td-text)]"
                        }`}>
                      <Icon size={13} className="shrink-0" />
                      <span className="font-serif text-[12px]">{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
              {navigateTail.map(renderLink)}
            </div>
          </div>

          <div className="mb-4">
            <div className="px-6 pb-2 font-mono text-[9px] uppercase tracking-widest text-[var(--td-text-3)]">
              {t("d_section_settings")}
            </div>
            <div className="space-y-0.5">
              {settings.map(renderLink)}
            </div>
          </div>
        </nav>

        <div className="p-4 hairline-top" style={{ background: "var(--td-surface)" }}>
          <div data-recording-sensitive="name" className="text-xs text-[var(--td-text-2)] mb-1 truncate">{user?.full_name}</div>
          <div data-recording-sensitive="email" className="font-mono text-[10px] text-[var(--td-text-3)] truncate mb-3">{user?.email}</div>
          <div className="mb-3"><ThemeSwitcher /></div>
          <div className="flex shrink-0 items-center gap-2">
            <button onClick={cycleLang} data-testid={TESTIDS.dashLangToggle}
                    aria-label={t("d_nav_language")}
                    className="inline-flex items-center gap-1 font-mono text-[10px] px-2 py-1 border border-[var(--td-border)] hover:border-[var(--td-gold)] hover:text-[var(--td-gold)] transition-colors uppercase tracking-widest">
              <Globe2 size={11} /> {lang.toUpperCase()}
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 relative pb-20 md:pb-24 min-w-0 max-w-full overflow-x-hidden">
        <header className="hidden xl:flex sticky top-0 z-40 min-h-14 px-6 xl:px-10 items-center justify-end gap-4 border-b border-[var(--td-border)] bg-[var(--td-bg)]/95 backdrop-blur-xl">
          <Link to="/demo" className="btn-gold !py-2 !px-4 inline-flex items-center gap-2 text-xs">
            <CirclePlay size={15} /> {t("d_nav_demo")}
          </Link>
          <div className="flex items-center gap-2 min-w-0 text-xs text-[var(--td-text-2)]">
            <UserCircle size={16} className="text-[var(--td-gold)] shrink-0" />
            <span data-recording-sensitive="name" className="max-w-56 truncate">{user?.full_name || user?.email}</span>
          </div>
          <button
            type="button"
            onClick={doLogout}
            data-testid={TESTIDS.dashNavLogout}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--td-border)] text-xs text-[var(--td-text-2)] hover:border-red-500 hover:text-red-400 transition-colors"
          >
            <LogOut size={14} /> {t("d_nav_logout")}
          </button>
        </header>
        <Outlet />
      </main>

      <OnboardingWizard />
    </div>
  );
}
